import { createStyles, Group, Loader, Modal, Stack } from '@mantine/core';
import type { EntityJoin } from '@repo/api-codegen';
import { useApiGetEntityJoins } from '@repo/api-codegen';
import type { FilterOption } from '@repo/common/components/Filter/types';
import {
	FilterDropdownType,
	FilterOperator,
	FilterOptionType,
	FilterType,
} from '@repo/common/components/Filter/types';
import { EntityType } from '@repo/common/enums/entityType';
import { UserRole } from '@repo/common/enums/UserRole';
import { Button } from '@repo/foundations';
import { ERDFlow, ERDFlowProvider } from '@repo/lineage';
import { Suspense, useState } from 'react';
import type { ISecodaEntity } from '../../api';
import { queryClient } from '../../api';
import {
	buildEntityGetItemsFn,
	buildGetItemsByIdFromSearch,
} from '../../components/Filter/filterConfigUtils';
import { QueryDrawerController } from '../../features/QueryDrawer/QueryDrawerController';
import { QueryDrawerProvider } from '../../features/QueryDrawer/QueryDrawerProviderController';
import { FeatureGuard } from '../FeatureGuard/FeatureGuard';
import { TableV2 } from '../TableV2';
import { useJoinColumns } from './hooks';
import { QueryTable } from './QueryTable';

const JOIN_FILTER_OPTIONS: FilterOption[] = [
	{
		label: 'Table',
		type: FilterOptionType.RESOURCE_ID,
		field: 'left_table', // NOTE: The backend filters on both left and right tables
		filterDropdownConfig: {
			searchPlaceholder: 'Search tables',
			multipleCountsSuffix: 'tables',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Multiple,
			getItems: buildEntityGetItemsFn(EntityType.table),
			getItemsById: buildGetItemsByIdFromSearch,
		},
	},
	{
		label: 'Column',
		type: FilterOptionType.RESOURCE_ID,
		field: 'left_column', // NOTE: The backend filters on both left and right columns
		filterDropdownConfig: {
			searchPlaceholder: 'Search columns',
			multipleCountsSuffix: 'columns',
			dropdownType: FilterDropdownType.List,
			defaultOperator: FilterOperator.Is,
			filterType: FilterType.Multiple,
			getItems: buildEntityGetItemsFn(EntityType.column),
			getItemsById: buildGetItemsByIdFromSearch,
		},
	},
];

export interface IQueryListProps {
	entity: ISecodaEntity;
}

const useStyles = createStyles(() => ({
	modalBody: {
		width: '100%',
		height: 'calc(90vh - 52.5px)',
	},
}));

export function QueryList({ entity }: IQueryListProps) {
	const { classes } = useStyles();

	const [view, setView] = useState<'queries' | 'joins'>('queries');
	const [isERDOpen, setIsERDOpen] = useState(false);

	const joinColumns = useJoinColumns();

	return (
		<FeatureGuard
			featureName="Queries"
			v1AllowedRoles={[
				UserRole.VIEWER,
				UserRole.GUEST,
				UserRole.ADMIN,
				UserRole.EDITOR,
			]}
			v2Permission="Queries.Read"
			isFeatureIncludedInPlan={true}
			isAlwaysIncludedFeature={false}
		>
			<QueryDrawerProvider>
				<Stack spacing="md">
					<Group w="100%" position="apart">
						<Group spacing="3xs">
							<Button
								variant="tertiary"
								onClick={() => setView('queries')}
								highlight={view === 'queries'}
							>
								Queries
							</Button>
							<Button
								variant="tertiary"
								onClick={() => setView('joins')}
								highlight={view === 'joins'}
							>
								Joins
							</Button>
						</Group>
						<Button
							variant="tertiary"
							leftIconName="map"
							onClick={() => setIsERDOpen(true)}
						>
							ERD
						</Button>
					</Group>
					{view === 'joins' && (
						<TableV2<EntityJoin>
							columns={joinColumns}
							pluralTypeString="joins"
							// @ts-ignore
							usePaginationList={useApiGetEntityJoins}
							usePaginationListOptions={{
								pathParams: {
									entityId: entity.id,
								},
							}}
							paginationSize={20}
							useCodegenListInterface
							withFilterOptions={JOIN_FILTER_OPTIONS}
							withInteractiveHeader
							withSearch
						/>
					)}
					{view === 'queries' && <QueryTable entity={entity} />}
					<ERDFlowProvider rootId={entity.id} queryClient={queryClient}>
						<Modal
							classNames={{
								body: classes.modalBody,
							}}
							opened={isERDOpen}
							onClose={() => setIsERDOpen(false)}
							title="ERD"
							size="90vw"
						>
							<ERDFlow />
						</Modal>
					</ERDFlowProvider>
					<Suspense fallback={<Loader />}>
						<QueryDrawerController />
					</Suspense>
				</Stack>
			</QueryDrawerProvider>
		</FeatureGuard>
	);
}
